import { useMemo } from 'react';
import { useGameStore } from '../store/gameStore';
import type { CityData } from '../types/city';

interface MinimapProps {
  cityData: CityData;
}

/** World bounds for mapping — covers the wider festival grounds */
const WORLD_MIN_X = -40;
const WORLD_MAX_X = 40;
const WORLD_MIN_Z = -22;
const WORLD_MAX_Z = 32;
const MAP_W = 160;
const MAP_H = 108;

function worldToMap(wx: number, wz: number): { x: number; y: number } {
  const x = ((wx - WORLD_MIN_X) / (WORLD_MAX_X - WORLD_MIN_X)) * MAP_W;
  const y = ((wz - WORLD_MIN_Z) / (WORLD_MAX_Z - WORLD_MIN_Z)) * MAP_H;
  return { x: Math.round(x), y: Math.round(y) };
}

/**
 * Minimap — overhead map showing stage positions, district zones, and the player.
 * Renders as an HTML overlay in the top-right corner.
 */
export default function Minimap({ cityData }: MinimapProps) {
  const playerPos = useGameStore((s) => s.playerPosition);
  const playerAngle = useGameStore((s) => s.playerRotation);

  const stageDots = useMemo(() => {
    return cityData.districts.flatMap((district) =>
      district.tracks.map((track, i) => {
        const cols = Math.ceil(Math.sqrt(district.tracks.length));
        const row = Math.floor(i / cols);
        const col = i % cols;
        const xRange = district.bounds.x[1] - district.bounds.x[0];
        const zRange = district.bounds.z[1] - district.bounds.z[0];
        const cellW = xRange / cols;
        const cellD = zRange / Math.ceil(district.tracks.length / cols);
        const x = district.bounds.x[0] + col * cellW + cellW / 2;
        const z = district.bounds.z[0] + row * cellD + cellD / 2;
        return { x, z, color: district.color, title: track.title };
      }),
    );
  }, [cityData]);

  const player = worldToMap(playerPos[0], playerPos[2]);

  return (
    <div
      className="fixed top-4 right-4 z-30 rounded-xl overflow-hidden border-2 border-white/20 shadow-lg"
      style={{ width: MAP_W + 4, height: MAP_H + 26 }}
    >
      {/* Map surface */}
      <svg width={MAP_W} height={MAP_H} className="block bg-black/50 backdrop-blur-sm">
        {/* District zones */}
        {cityData.districts.map((d) => {
          const topLeft = worldToMap(d.bounds.x[0], d.bounds.z[0]);
          const botRight = worldToMap(d.bounds.x[1], d.bounds.z[1]);
          return (
            <rect
              key={d.id}
              x={topLeft.x}
              y={Math.min(topLeft.y, botRight.y)}
              width={Math.max(botRight.x - topLeft.x, 1)}
              height={Math.max(botRight.y - topLeft.y, 1)}
              fill={d.color}
              opacity={0.15}
              rx={4}
            />
          );
        })}

        {/* Stages */}
        {stageDots.map((b, i) => {
          const p = worldToMap(b.x, b.z);
          return (
            <rect
              key={i}
              x={p.x - 3}
              y={p.y - 3}
              width={6}
              height={6}
              fill={b.color}
              opacity={0.8}
              rx={1}
            >
              <title>{b.title}</title>
            </rect>
          );
        })}

        {/* Player dot */}
        <g transform={`translate(${player.x}, ${player.y})`}>
          <circle r={4} fill="#FFD700" opacity={0.9} />
          {/* Direction indicator */}
          <line
            x1={0}
            y1={0}
            x2={Math.sin(playerAngle) * 6}
            y2={Math.cos(playerAngle) * 6}
            stroke="#FFD700"
            strokeWidth={2}
            opacity={0.8}
          />
        </g>
      </svg>

      {/* District labels */}
      <div className="bg-black/60 backdrop-blur-sm px-2 py-1.5 flex gap-3 flex-wrap">
        {cityData.districts.map((d) => (
          <div key={d.id} className="flex items-center gap-1">
            <span
              className="w-2.5 h-2.5 rounded-sm inline-block"
              style={{ backgroundColor: d.color }}
            />
            <span className="text-white/70 text-[10px] font-medium">{d.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
